// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * The BOM import's preflight report (design doc §8.2; component design §4.6.7).
 *
 * Read top to bottom the way a process engineer checks a file: what was read (format,
 * sheet, the machine's part number, the name it suggests), what blocks the import (errors,
 * located by row), and what imports but deserves a look (warnings, grouped by kind and
 * opened to their rows). Warnings never block — but the import button stays off until
 * "I have checked these" is ticked, because 86 skipped rows nobody looked at is how a BOM
 * loses parts without anyone deciding it should.
 *
 * Rows are located the way their authors know them — 序号, 安装位置, 料号, 描述 — since
 * the sheet row number means little in a 489-row workbook with hidden rows.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { BomPreflight, ImportIssue } from "@/api/types";

const ISSUE_ROW_CAP = 50;

interface BomPreflightReportProps {
  report: BomPreflight;
  committing: boolean;
  onCommit: (acknowledgedWarnings: boolean) => void;
  /** Re-run the preflight on another sheet of the same workbook. */
  onChooseSheet: (sheet: string) => void;
}

export function BomPreflightReport({
  report,
  committing,
  onCommit,
  onChooseSheet,
}: BomPreflightReportProps) {
  const { t } = useTranslation();
  const [acknowledged, setAcknowledged] = useState(false);
  const passed = report.status === "VALIDATED";
  // Absent from a report produced before the engineering format; read as empty.
  const warnings = report.warnings ?? [];
  const sheets = report.sheets ?? [];
  const ignored = report.ignored_columns ?? [];
  const needsAck = warnings.length > 0;

  return (
    <div className={`banner ${passed ? "banner-info" : "banner-error"}`}>
      <h2>{t("bom.preflightTitle")}</h2>

      <dl className="facts">
        {report.format && (
          <>
            <dt>{t("bom.importFormat")}</dt>
            <dd>{t(`bom.importFormats.${report.format}`)}</dd>
          </>
        )}
        {report.sheet && (
          <>
            <dt>{t("bom.importSheet")}</dt>
            <dd>
              {sheets.length > 1 ? (
                <select
                  aria-label={t("bom.importSheet")}
                  value={report.sheet}
                  onChange={(event) => onChooseSheet(event.target.value)}
                >
                  {sheets.map((sheet) => (
                    <option key={sheet} value={sheet}>
                      {sheet}
                    </option>
                  ))}
                </select>
              ) : (
                report.sheet
              )}
            </dd>
          </>
        )}
        {report.root_material_code && (
          <>
            <dt>{t("bom.rootMaterialCode")}</dt>
            <dd className="mono">{report.root_material_code}</dd>
          </>
        )}
        {report.suggested_name && (
          <>
            <dt>{t("bom.suggestedName")}</dt>
            <dd>{report.suggested_name}</dd>
          </>
        )}
      </dl>

      {passed ? (
        <p>
          {t("bom.preflightPassed", {
            rows: report.row_count,
            slots: report.required_sn_count,
          })}
        </p>
      ) : (
        <>
          <p>{t("bom.preflightFailed", { count: report.issues.length })}</p>
          <IssueTable issues={report.issues} />
        </>
      )}

      {passed && (report.modules ?? []).length > 0 && (
        <section>
          <h3>{t("bom.suggestedModules")}</h3>
          <table>
            <thead>
              <tr>
                <th>{t("bom.modules.module")}</th>
                <th>{t("bom.modules.assembly")}</th>
                <th>{t("bom.modules.lines")}</th>
                <th>{t("bom.modules.scans")}</th>
              </tr>
            </thead>
            <tbody>
              {(report.modules ?? []).map((module) => (
                <tr key={module.position_code}>
                  <td style={{ paddingLeft: `${module.depth * 16}px` }}>
                    <span className="module-badge">{module.module_type}</span>
                  </td>
                  <td>
                    <span className="mono">{module.position_code}</span> {module.material_info}
                    {module.part_name && <span className="hint"> · {module.part_name}</span>}
                  </td>
                  <td>{module.line_count}</td>
                  <td>{module.scan_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* What the SN example column will become: one rule per label format of a material,
          shared by every line of it. Shown before importing so a wrong example is caught
          here rather than at the first scan. */}
      {passed && (report.barcode_rules ?? []).length > 0 && (
        <section>
          <h3>{t("bom.importedBarcodeRules", { n: (report.barcode_rules ?? []).length })}</h3>
          <table className="barcode-rules">
            <thead>
              <tr>
                <th>{t("bom.materialCode")}</th>
                <th>{t("barcodeRules.example")}</th>
                <th>{t("barcodeRules.pattern")}</th>
              </tr>
            </thead>
            <tbody>
              {(report.barcode_rules ?? []).map((rule) => (
                <tr key={`${rule.material_code} ${rule.pattern}`}>
                  <td className="mono">{rule.material_code}</td>
                  <td className="mono">{rule.example}</td>
                  <td className="mono">{rule.pattern}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="hint">{t("bom.importedBarcodeRulesHint")}</p>
        </section>
      )}

      {warnings.length > 0 && (
        <section>
          <h3>{t("bom.warningsTitle", { count: warnings.length })}</h3>
          {groupByCode(warnings).map(([code, group]) => (
            <details key={code}>
              <summary title={code}>
                {group[0]?.message} · {group.length}
              </summary>
              <IssueTable issues={group} />
            </details>
          ))}
        </section>
      )}

      {ignored.length > 0 && (
        <p className="hint">{t("bom.ignoredColumns", { columns: ignored.join("、") })}</p>
      )}

      {passed && (
        <div className="filters">
          {needsAck && (
            <label>
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(event) => setAcknowledged(event.target.checked)}
              />{" "}
              {t("bom.acknowledgeWarnings")}
            </label>
          )}
          <button
            type="button"
            disabled={committing || (needsAck && !acknowledged)}
            onClick={() => onCommit(needsAck && acknowledged)}
          >
            {t("bom.commitImport")}
          </button>
        </div>
      )}
    </div>
  );
}

function groupByCode(issues: ImportIssue[]): [string, ImportIssue[]][] {
  const groups = new Map<string, ImportIssue[]>();
  for (const issue of issues) {
    const group = groups.get(issue.code) ?? [];
    group.push(issue);
    groups.set(issue.code, group);
  }
  return [...groups.entries()];
}

function IssueTable({ issues }: { issues: ImportIssue[] }) {
  const { t } = useTranslation();
  // An engineering BOM locates rows by 序号 and part number; the old template by the
  // sheet row and column.
  const located = issues.some((issue) => issue.context);
  return (
    <>
      <table>
        <thead>
          <tr>
            <th>{t("bom.issueRow")}</th>
            {located ? (
              <>
                <th>{t("bom.issueSeq")}</th>
                <th>{t("bom.issueLocation")}</th>
                <th>{t("bom.issueMaterialCode")}</th>
                <th>{t("bom.issueDescription")}</th>
              </>
            ) : (
              <th>{t("bom.issueColumn")}</th>
            )}
            <th>{t("bom.issueCode")}</th>
            <th>{t("bom.issueValue")}</th>
          </tr>
        </thead>
        <tbody>
          {/* Capped: a file with a wrong header produces one issue per row, and a
              thousand identical lines hide the one that explains them. */}
          {issues.slice(0, ISSUE_ROW_CAP).map((issue, index) => (
            <tr key={index}>
              <td>{issue.row ?? "—"}</td>
              {located ? (
                <>
                  <td>{issue.context?.seq ?? "—"}</td>
                  <td>{issue.context?.install_location ?? "—"}</td>
                  <td className="mono">{issue.context?.material_code ?? "—"}</td>
                  <td>{issue.context?.description ?? "—"}</td>
                </>
              ) : (
                <td>{issue.column_label ?? "—"}</td>
              )}
              {/* The code stays on hover: it is what support searches for. */}
              <td title={issue.code}>{issue.message}</td>
              <td className="mono">{issue.value ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {issues.length > ISSUE_ROW_CAP && <p className="hint">+{issues.length - ISSUE_ROW_CAP}</p>}
    </>
  );
}
